import { useCallback, useEffect, useState } from 'react';
import { api, errMsg } from '../lib/api';

interface PresetItem {
  id: string;
  zh: string;
  en: string;
}

interface MyQuestion {
  id: string;
  presetId: string;
  question: string;
  isCustom: boolean;
}

export default function SecuritySection() {
  const [presets, setPresets] = useState<PresetItem[]>([]);
  const [mine, setMine] = useState<MyQuestion[]>([]);
  const [maxQuestions, setMaxQuestions] = useState(3);

  const [mode, setMode] = useState<'preset' | 'custom'>('preset');
  const [presetId, setPresetId] = useState('');
  const [customQuestion, setCustomQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    const [p, m] = await Promise.all([
      api<{ items: PresetItem[]; maxQuestions: number }>('/security/presets'),
      api<{ items: MyQuestion[]; maxQuestions: number }>('/security/questions'),
    ]);
    if (p.ok && p.data) {
      setPresets(p.data.items);
      setMaxQuestions(p.data.maxQuestions);
      if (p.data.items.length > 0 && !presetId) setPresetId(p.data.items[0].id);
    }
    if (m.ok && m.data) setMine(m.data.items);
  }, [presetId]);

  useEffect(() => {
    load();
  }, []);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setMsg('');
    setLoading(true);

    const payload =
      mode === 'preset'
        ? { presetId, answer }
        : { presetId: 'custom', question: customQuestion, answer };

    const res = await api('/security/questions', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    setLoading(false);

    if (res.ok) {
      setMsg('密保问题已添加');
      setCustomQuestion('');
      setAnswer('');
      load();
    } else {
      setError(errMsg(res, '添加失败'));
    }
  }

  async function remove(id: string) {
    if (!confirm('删除这个密保问题？')) return;
    const res = await api(`/security/questions/${id}`, { method: 'DELETE' });
    if (res.ok) {
      setMsg('已删除');
      load();
    } else {
      setError(errMsg(res, '删除失败'));
    }
  }

  const full = mine.length >= maxQuestions;

  return (
    <section className="mb-6 rounded-2xl border border-ink-200 bg-white/80 p-6">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="text-sm font-medium text-ink-900">密保问题</h2>
        <span className="text-[11px] text-ink-500">
          {mine.length}/{maxQuestions}
        </span>
      </div>
      <p className="mb-4 text-[11px] leading-relaxed text-ink-400">
        忘记密码时可通过回答密保问题重置。答案不区分大小写和空格，但需与设置时一致。
      </p>

      {mine.length > 0 && (
        <div className="mb-4 space-y-2">
          {mine.map((q) => (
            <div
              key={q.id}
              className="flex items-center justify-between rounded-xl border border-ink-200 bg-ink-50/50 px-4 py-2.5"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-ink-800">{q.question}</p>
                {q.isCustom && (
                  <span className="text-[10px] text-ink-400">自定义</span>
                )}
              </div>
              <button
                onClick={() => remove(q.id)}
                className="ml-3 shrink-0 text-xs text-red-600 underline"
              >
                删除
              </button>
            </div>
          ))}
        </div>
      )}

      {full ? (
        <p className="rounded-lg bg-ink-50 px-3 py-2.5 text-xs text-ink-500">
          已达上限。如需更换，请先删除一个再添加。
        </p>
      ) : (
        <form onSubmit={add}>
          <div className="mb-3 flex gap-2">
            <button
              type="button"
              onClick={() => setMode('preset')}
              className={`h-9 flex-1 rounded-xl border text-xs transition ${
                mode === 'preset'
                  ? 'border-ink-900 bg-ink-900 text-ink-50'
                  : 'border-ink-200 text-ink-600'
              }`}
            >
              选择内置问题
            </button>
            <button
              type="button"
              onClick={() => setMode('custom')}
              className={`h-9 flex-1 rounded-xl border text-xs transition ${
                mode === 'custom'
                  ? 'border-ink-900 bg-ink-900 text-ink-50'
                  : 'border-ink-200 text-ink-600'
              }`}
            >
              自定义问题
            </button>
          </div>

          {mode === 'preset' ? (
            <div className="mb-3">
              <label className="mb-1.5 block text-xs text-ink-600">选择问题</label>
              <select
                value={presetId}
                onChange={(e) => setPresetId(e.target.value)}
                className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500 focus:bg-white"
              >
                {presets.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.zh}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="mb-3">
              <label className="mb-1.5 block text-xs text-ink-600">你的问题</label>
              <input
                value={customQuestion}
                onChange={(e) => setCustomQuestion(e.target.value)}
                placeholder="例如：我第一辆车的车牌号是？"
                className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500 focus:bg-white"
              />
            </div>
          )}

          <div className="mb-4">
            <label className="mb-1.5 block text-xs text-ink-600">答案</label>
            <input
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="设置一个只有你知道的答案"
              className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500 focus:bg-white"
            />
          </div>

          {error && (
            <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">{error}</p>
          )}
          {msg && (
            <p className="mb-3 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
              {msg}
            </p>
          )}

          <button
            type="submit"
            disabled={loading || !answer}
            className="h-11 rounded-xl bg-ink-900 px-6 text-sm text-ink-50 transition hover:bg-ink-800 disabled:opacity-40"
          >
            {loading ? '保存中…' : '添加密保'}
          </button>
        </form>
      )}
    </section>
  );
}
